import { useAuth } from 'contexts/AuthContext';
import { useEffect, useRef, useState } from 'react';
import { userService } from 'api';
import ProfilePicture from './ProfilePicture';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function ProfilePictureManager() {
  const { user, refreshUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasProfilePicture = Boolean(user?.profilePictureUrl) || Boolean(previewUrl);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const clearPreview = () => {
    if (previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPendingFile(null);
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'İzin verilen türler: JPEG, PNG veya WebP.';
    }
    if (file.size > MAX_BYTES) {
      return 'Dosya boyutu en fazla 5 MB olabilir.';
    }
    return null;
  };

  const handleFileSelected = (file: File) => {
    setError(null);
    setSuccess(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    clearPreview();
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setPendingFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) handleFileSelected(file);
  };

  const handleCancelPreview = () => {
    clearPreview();
    setError(null);
  };

  const handleSave = async () => {
    if (!pendingFile || !user?.id) return;
    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await userService.uploadProfilePicture(pendingFile);
      if (result.isSuccess) {
        clearPreview();
        await refreshUser();
        setSuccess('Profil fotoğrafı güncellendi.');
      } else {
        setError(result.message ?? 'Profil fotoğrafı yüklenemedi.');
      }
    } catch {
      setError('Profil fotoğrafı yüklenirken bir hata oluştu.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!user?.id) return;
    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await userService.deleteProfilePicture();
      if (result.isSuccess) {
        clearPreview();
        await refreshUser();
        setSuccess('Profil fotoğrafı kaldırıldı.');
      } else {
        setError(result.message ?? 'Profil fotoğrafı kaldırılamadı.');
      }
    } catch {
      setError('Profil fotoğrafı kaldırılırken bir hata oluştu.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="profile-photo mb-3 position-relative d-inline-block">
      <ProfilePicture width={150} height={150} fallbackSize="4x" overrideSrc={previewUrl} />
      <button
        type="button"
        className="photo-edit-icon"
        title="Fotoğraf yükle"
        aria-label="Profil fotoğrafını düzenle"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fas fa-camera" aria-hidden />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        className="d-none"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleInputChange}
        disabled={uploading}
      />

      {pendingFile && (
        <div className="mt-3 d-flex flex-wrap gap-2 justify-content-center">
          <button type="button" className="btn btn-primary btn-sm" disabled={uploading} onClick={handleSave}>
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden />
                Kaydediliyor…
              </>
            ) : (
              'Kaydet'
            )}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={uploading}
            onClick={handleCancelPreview}
          >
            İptal
          </button>
        </div>
      )}

      {hasProfilePicture && !pendingFile && (
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={uploading}
            onClick={handleRemovePhoto}
          >
            Fotoğrafı kaldır
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger py-2 small mt-2 mb-0" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 small mt-2 mb-0" role="status">
          {success}
        </div>
      )}
    </div>
  );
}
